import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import API_ENDPOINTS, { getImageUrl } from '../config/api';
import { parsePledgeAmountInput } from '../utils/formatPledgeAmount';
import { PHONE_COUNTRIES, validatePledgePhone } from '../utils/pledgePhone';
import { downloadPledgeCardPdf } from '../utils/downloadPledgeCard';
import PledgeCard from './PledgeCard';
import PledgeCardViewport from './PledgeCardViewport';
import './PledgePage.css';

const REMINDER_OPTIONS = [
  { value: '1_week', label: 'Remind me in 1 week' },
  { value: '2_weeks', label: 'Remind me in 2 weeks' },
  { value: '3_weeks', label: 'Remind me in 3 weeks' },
];

const PLEDGE_CURRENCIES = [
  { value: 'KES', label: 'KES — Kenya Shilling' },
  { value: 'UGX', label: 'UGX — Uganda Shilling' },
  { value: 'USD', label: 'USD — US Dollar' },
];

const pledgeCoupleLabel = (names) =>
  String(names || 'our wedding').replace(/Vitalis's?/gi, 'vitalis');

const pledgeIntroText = (settings, coupleNames) => {
  const fallback = `Join us in celebrating this special journey by making a pledge to support ${pledgeCoupleLabel(coupleNames)} wedding.`;
  const raw = settings?.pledge_intro_text?.trim() || fallback;
  return raw.replace(/Vitalis's/gi, 'vitalis');
};

const PledgePage = ({ settings, onClose }) => {
  const [step, setStep] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [pledge, setPledge] = useState(null);
  const cardRef = useRef(null);

  const coupleNames = settings?.couple_names || 'Our Wedding';
  const intro = pledgeIntroText(settings, coupleNames);

  const themeStyle = {
    '--primary': settings?.theme_primary_color || '#c9a03d',
    '--secondary': settings?.theme_secondary_color || '#d4af37',
    '--dark': '#1a1a2e',
    '--light': '#faf6f0',
  };

  const defaultCurrency = settings?.pledge_currency || 'KES';

  const [form, setForm] = useState({
    guest_name: '',
    guest_email: '',
    phone_country: '+254',
    phone_local: '',
    currency: defaultCurrency,
    amount: '',
    reminder_option: '2_weeks',
  });

  const selectedCountry =
    PHONE_COUNTRIES.find((c) => c.code === form.phone_country) || PHONE_COUNTRIES[0];

  const handleAmountChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, '');
    setForm((f) => ({ ...f, amount: digits }));
  };

  const handlePhoneLocalChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, selectedCountry.localDigits);
    setForm((f) => ({ ...f, phone_local: digits }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const phoneCheck = validatePledgePhone(form.phone_country, form.phone_local);
    if (!phoneCheck.valid) {
      setError(phoneCheck.message);
      return;
    }

    const amount = parsePledgeAmountInput(form.amount);
    if (amount === null || amount <= 0) {
      setError('Please enter a pledge amount.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(API_ENDPOINTS.SUBMIT_PLEDGE, {
        guest_name: form.guest_name.trim(),
        guest_email: form.guest_email.trim(),
        guest_phone: phoneCheck.full,
        amount,
        currency: form.currency,
        reminder_option: form.reminder_option,
      });
      if (res.data.success) {
        setPledge(res.data.pledge);
        setDownloadError('');
        setStep('success');
      } else {
        setError(res.data.message || 'Could not submit pledge');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.message === 'Network Error'
          ? 'Could not reach the server. Check your connection and try again.'
          : 'Something went wrong. Please try again.');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    setDownloadError('');
    setDownloadSuccess(false);
    setDownloading(true);
    try {
      await downloadPledgeCardPdf(
        cardRef.current,
        `pledge-card-${pledge?.pledge_reference || 'wedding'}.pdf`
      );
      setDownloadSuccess(true);
    } catch (err) {
      setDownloadError(
        err?.message ||
          'Could not download the card. Try again, or use your browser print (Ctrl+P) on this page.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const resetForm = () => {
    setStep('form');
    setPledge(null);
    setError('');
    setDownloadError('');
    setDownloadSuccess(false);
    setForm({
      guest_name: '',
      guest_email: '',
      phone_country: '+254',
      phone_local: '',
      currency: defaultCurrency,
      amount: '',
      reminder_option: '2_weeks',
    });
  };

  const heroImage = settings?.pledge_couple_image;
  const cardImage = settings?.pledge_card_image || settings?.pledge_couple_image;

  const previewAmount = parsePledgeAmountInput(form.amount);
  const previewPledge = {
    guest_name: form.guest_name.trim() || 'Your Name Here',
    amount: previewAmount !== null ? previewAmount : 150000,
    currency: form.currency || defaultCurrency,
    couple_names: coupleNames,
    card_image: cardImage,
    bank_details: settings?.pledge_bank_details,
    contact_info: settings?.pledge_contact_info,
    reminder_date_formatted: '',
  };

  return (
    <div className="pledge-page" style={themeStyle}>
      <button type="button" className="pledge-close" onClick={onClose} aria-label="Close">
        ×
      </button>

      <div
        className="pledge-hero-bg"
        style={heroImage ? { backgroundImage: `url(${getImageUrl(heroImage)})` } : undefined}
      />

      <div className="pledge-page-header">
        {heroImage && (
          <div className="pledge-avatar">
            <img src={getImageUrl(heroImage)} alt={coupleNames} />
          </div>
        )}
        <h1>{coupleNames}</h1>
        <p className="pledge-page-tag">Pledge Card</p>
      </div>

      <AnimatePresence mode="wait">
        {step === 'form' ? (
          <motion.div
            key="form"
            className="pledge-form-layout"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="pledge-form-panel">
              <h2>Make Your Pledge</h2>
              <p className="pledge-intro">{intro}</p>
              {(settings?.pledge_bank_details || settings?.pledge_contact_info) && (
                <div className="pledge-payment-info">
                  {settings.pledge_bank_details && (
                    <p>
                      <strong>Payment details:</strong> {settings.pledge_bank_details}
                    </p>
                  )}
                  {settings.pledge_contact_info && (
                    <p>
                      <strong>Contact:</strong> {settings.pledge_contact_info}
                    </p>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="pledge-field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    required
                    value={form.guest_name}
                    onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                    placeholder="Your full name"
                  />
                </div>
                <div className="pledge-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    required
                    value={form.guest_email}
                    onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                    placeholder="you@email.com"
                  />
                </div>
                <div className="pledge-field">
                  <label>Phone Number *</label>
                  <div className="pledge-phone-row">
                    <select
                      value={form.phone_country}
                      onChange={(e) =>
                        setForm({ ...form, phone_country: e.target.value, phone_local: '' })
                      }
                      aria-label="Country code"
                    >
                      {PHONE_COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      required
                      inputMode="numeric"
                      value={form.phone_local}
                      onChange={handlePhoneLocalChange}
                      placeholder={selectedCountry.placeholder}
                      maxLength={selectedCountry.localDigits}
                    />
                  </div>
                  <p className="pledge-field-hint">
                    Full number: {form.phone_country}
                    {form.phone_local || '…'}
                  </p>
                </div>
                <div className="pledge-field">
                  <label>Currency *</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    required
                  >
                    {PLEDGE_CURRENCIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pledge-field">
                  <label>Pledge Amount *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={form.amount}
                    onChange={handleAmountChange}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="pledge-field">
                  <label>When should we remind you? *</label>
                  <select
                    value={form.reminder_option}
                    onChange={(e) => setForm({ ...form, reminder_option: e.target.value })}
                    required
                  >
                    {REMINDER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {error && <p className="pledge-error">{error}</p>}

                <button type="submit" className="pledge-submit" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit Pledge'}
                </button>
              </form>
            </div>

            <div className="pledge-preview-panel">
              <p className="pledge-preview-label">Card preview</p>
              <PledgeCardViewport>
                <PledgeCard pledge={previewPledge} settings={settings} />
              </PledgeCardViewport>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            className="pledge-success-panel"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2>Your Pledge Card</h2>
            <p className="pledge-success-thanks">Thank you for your generous support.</p>

            <PledgeCardViewport>
              <PledgeCard ref={cardRef} pledge={pledge} settings={settings} />
            </PledgeCardViewport>

            <div className="pledge-success-actions">
              <button
                type="button"
                className="pledge-btn-primary"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? 'Preparing PDF…' : 'Download PDF'}
              </button>
              <button type="button" className="pledge-btn-secondary" onClick={resetForm}>
                Make Another Pledge
              </button>
            </div>
            {downloadSuccess && (
              <p className="pledge-download-success" role="status">
                Downloaded successfully
              </p>
            )}
            {downloadError && <p className="pledge-error">{downloadError}</p>}
            {pledge?.guest_email && (
              <p className="pledge-email-note">
                A confirmation has been sent to <strong>{pledge.guest_email}</strong>.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PledgePage;
